import {
  ArrowLeftIcon,
  CalendarIcon,
  Edit,
  PlusIcon,
  StethoscopeIcon,
  Trash2Icon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@virtual-care/shadcn';
import { client } from '@virtual-care/ui';

interface Command {
  id: string;
  label: string;
  section: string;
  icon: React.ComponentType<{ className?: string }>;
  onSelect: (...params: any) => void;
  nextPlaceholder?: string;
}

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useHotkeys('mod+k', () => setOpen((open) => !open));
  const [value, setValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [actionStack, setActionStack] = useState<
    | {
        onValueChange: (value: string) => void;
        placeholder?: string;
      }[]
    | undefined
  >();
  const originalActions: Command[] = [
    {
      id: 'create-doctor',
      label: 'Add Doctor',
      section: 'create',
      icon: PlusIcon,
      onSelect: () => {
        navigate('/doctors', {
          state: { action: 'create' },
        });
        setOpen(false);
        resetState();
      },
    },
    {
      id: 'create-speciality',
      label: 'Add Speciality',
      icon: PlusIcon,
      section: 'create',
      onSelect: () => {
        navigate('/specialities', {
          state: { action: 'create' },
        });
        setOpen(false);
        resetState();
      },
    },
    {
      id: 'update-speciality',
      label: 'Update Speciality',
      icon: Edit,
      section: 'update',
      async onSelect() {
        setActionStack((prev) => [
          ...(prev ?? []),
          {
            onValueChange: async (value) => {
              try {
                setIsLoading(true);
                const result = await client.request('GET /specialities', {
                  page: 1,
                  pageSize: 100,
                  query: value,
                });
                const records = result.records.map((record) => ({
                  label: record.name,
                  id: record.id,
                }));
                setActions(
                  records.map((record) => ({
                    id: `update-speciality-${record.id}`,
                    label: `Update ${record.label}`,
                    icon: Edit,
                    section: 'update',
                    onSelect: () => {
                      navigate('/specialities', {
                        state: { action: 'update', recordId: record.id },
                      });
                      setOpen(false);
                      resetState();
                    },
                  })),
                );
              } catch (error) {
                console.error(error);
                setActions([]);
              } finally {
                setIsLoading(false);
              }
            },
            placeholder: 'Search for specialities...',
          },
        ]);
      },
    },
    {
      id: 'update-doctor',
      label: 'Update Doctor',
      icon: Edit,
      section: 'update',
      async onSelect() {
        setActionStack((prev) => [
          ...(prev ?? []),
          {
            onValueChange: async (value) => {
              try {
                setIsLoading(true);
                const result = await client.request('GET /doctors', {
                  page: 1,
                  pageSize: 100,
                  query: value,
                });
                const records = result.records.map((record) => ({
                  label: `${record.firstName} ${record.lastName}`,
                  id: record.id,
                }));
                setActions(
                  records.map((record) => ({
                    id: `update-doctor-${record.id}`,
                    label: `Update ${record.label}`,
                    icon: Edit,
                    section: 'update',
                    onSelect: () => {
                      navigate('/doctors', {
                        state: { action: 'update', recordId: record.id },
                      });
                      setOpen(false);
                      resetState();
                    },
                  })),
                );
              } catch (error) {
                console.error(error);

                setActions([]);
              } finally {
                setIsLoading(false);
              }
            },
            placeholder: 'Search for doctors...',
          },
        ]);
      },
    },
    {
      id: 'update-coordinator',
      label: 'Update Coordinator',
      icon: Edit,
      section: 'update',
      async onSelect() {
        setActionStack((prev) => [
          ...(prev ?? []),
          {
            onValueChange: async (value) => {
              try {
                setIsLoading(true);
                const result = await client.request('GET /coordinators', {
                  page: 1,
                  pageSize: 100,
                  query: value,
                });
                const records = result.records.map((record) => ({
                  label: `${record.firstName} ${record.lastName}`,
                  id: record.id,
                }));
                setActions(
                  records.map((record) => ({
                    id: `update-coordinator-${record.id}`,
                    label: `Update ${record.label}`,
                    icon: Edit,
                    section: 'update',
                    onSelect: () => {
                      navigate('/coordinators', {
                        state: { action: 'update', recordId: record.id },
                      });
                      setOpen(false);
                      resetState();
                    },
                  })),
                );
              } catch (error) {
                console.error(error);
                setActions([]);
              } finally {
                setIsLoading(false);
              }
            },
            placeholder: 'Search for coordinators...',
          },
        ]);
      },
    },
    {
      id: 'delete-speciality',
      label: 'Delete Speciality',
      icon: Trash2Icon,
      section: 'delete',
      async onSelect() {
        setValue('');
        setActions([]);
        setActionStack((prev) => [
          ...(prev ?? []),
          {
            onValueChange: async (value) => {
              try {
                setIsLoading(true);
                const result = await client.request('GET /specialities', {
                  page: 1,
                  pageSize: 100,
                  query: value,
                });
                const records = result.records.map((record) => ({
                  label: record.name,
                  id: record.id,
                }));
                setActions(
                  records.map((record) => ({
                    id: `delete-speciality-${record.id}`,
                    label: `Delete ${record.label}`,
                    icon: Trash2Icon,
                    section: 'delete',
                    onSelect: async () => {
                      // TODO: Replace with proper confirmation dialog
                      const confirmed = window.confirm(
                        `Are you sure you want to delete ${record.label}?`,
                      );
                      if (confirmed) {
                        try {
                          await client.request('DELETE /specialities/{id}', {
                            id: record.id,
                          });
                          setOpen(false);
                          resetState();
                        } catch (error) {
                          console.error(error);
                          // TODO: Replace with proper error notification
                          window.alert(
                            'Failed to delete speciality. Please try again.',
                          );
                        }
                      }
                    },
                  })),
                );
              } catch (error) {
                console.error(error);

                setActions([]);
              } finally {
                setIsLoading(false);
              }
            },
            placeholder: 'Search for specialities to delete...',
          },
        ]);
      },
    },
    {
      id: 'delete-doctor',
      label: 'Delete Doctor',
      icon: Trash2Icon,
      section: 'delete',
      async onSelect() {
        setActionStack((prev) => [
          ...(prev ?? []),
          {
            onValueChange: async (value) => {
              try {
                setIsLoading(true);
                const result = await client.request('GET /doctors', {
                  page: 1,
                  pageSize: 100,
                  query: value,
                });
                const records = result.records.map((record) => ({
                  label: `${record.firstName} ${record.lastName}`,
                  id: record.id,
                }));
                setActions(
                  records.map((record) => ({
                    id: `delete-doctor-${record.id}`,
                    label: `Delete ${record.label}`,
                    icon: Trash2Icon,
                    section: 'delete',
                    onSelect: async () => {
                      // TODO: Replace with proper confirmation dialog
                      const confirmed = window.confirm(
                        `Are you sure you want to delete ${record.label}?`,
                      );
                      if (confirmed) {
                        try {
                          await client.request('DELETE /doctors/{id}', {
                            id: record.id,
                          });
                          setOpen(false);
                          resetState();
                        } catch (error) {
                          console.error(error);

                          // TODO: Replace with proper error notification
                          window.alert(
                            'Failed to delete doctor. Please try again.',
                          );
                        }
                      }
                    },
                  })),
                );
              } catch (error) {
                console.error(error);
                setActions([]);
              } finally {
                setIsLoading(false);
              }
            },
            placeholder: 'Search for doctors to delete...',
          },
        ]);
      },
    },
    {
      id: 'delete-coordinator',
      label: 'Delete Coordinator',
      icon: Trash2Icon,
      section: 'delete',
      async onSelect() {
        setValue('');
        setActionStack((prev) => [
          ...(prev ?? []),
          {
            onValueChange: async (value) => {
              try {
                setIsLoading(true);
                const result = await client.request('GET /coordinators', {
                  page: 1,
                  pageSize: 100,
                  query: value,
                });
                const records = result.records.map((record) => ({
                  label: `${record.firstName} ${record.lastName}`,
                  id: record.id,
                }));
                setActions(
                  records.map((record) => ({
                    id: `delete-coordinator-${record.id}`,
                    label: `Delete ${record.label}`,
                    icon: Trash2Icon,
                    section: 'delete',
                    onSelect: async () => {
                      // TODO: Replace with proper confirmation dialog
                      const confirmed = window.confirm(
                        `Are you sure you want to delete ${record.label}?`,
                      );
                      if (confirmed) {
                        try {
                          await client.request('DELETE /coordinators/{id}', {
                            id: record.id,
                          });
                          setOpen(false);
                          resetState();
                        } catch (error) {
                          console.error(error);
                          // TODO: Replace with proper error notification
                          window.alert(
                            'Failed to delete coordinator. Please try again.',
                          );
                        }
                      }
                    },
                  })),
                );
              } catch (error) {
                console.error(error);
                setActions([]);
              } finally {
                setIsLoading(false);
              }
            },
            placeholder: 'Search for coordinators to delete...',
          },
        ]);
      },
    },
    {
      id: 'create-coordinator',
      label: 'Add Coordinator',
      icon: PlusIcon,
      section: 'create',
      onSelect: () => {
        navigate('/coordinators', {
          state: { action: 'create' },
        });
        setOpen(false);
      },
    },
    {
      id: 'nav-patients',
      label: 'Go to Patients',
      section: 'navigation',
      icon: UserIcon,
      onSelect: () => {
        navigate('/patients');
        setOpen(false);
      },
    },
    {
      id: 'nav-doctors',
      label: 'Go to Doctors',
      section: 'navigation',
      icon: StethoscopeIcon,
      onSelect: () => {
        navigate('/doctors');
        setOpen(false);
      },
    },
    {
      id: 'nav-specialities',
      label: 'Go to Specialities',
      section: 'navigation',
      icon: CalendarIcon,
      onSelect: () => {
        navigate('/specialities');
        setOpen(false);
      },
    },
    {
      id: 'nav-coordinators',
      label: 'Go to Coordinators',
      section: 'navigation',
      icon: UsersIcon,
      onSelect: () => {
        navigate('/coordinators');
        setOpen(false);
      },
    },
  ] satisfies Command[];

  const [actions, setActions] = useState<Command[]>(originalActions);

  const goBack = useCallback(() => {
    setActionStack((prev) => {
      if (!prev || prev.length <= 1) {
        setActions(originalActions);
        return undefined;
      }
      return prev.slice(0, -1);
    });
  }, [originalActions]);

  const resetState = useCallback(() => {
    setActionStack(undefined);
    setActions(originalActions);
    setIsLoading(false);
  }, [originalActions]);

  const placeholder =
    'Search for actions, e.g. "Add Doctor", "Go to Patients"...';

  // Group actions by section
  const groupedActions = actions.reduce(
    (acc, action) => {
      if (!acc[action.section]) {
        acc[action.section] = [];
      }
      acc[action.section].push(action);
      return acc;
    },
    {} as Record<string, Command[]>,
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          resetState();
        }
      }}
      title="Command Palette"
      description="Search for actions and navigate quickly..."
    >
      <CommandInput
        value={value}
        onValueChange={(query) => {
          actionStack?.[actionStack.length - 1]?.onValueChange(query);
          setValue(query);
        }}
        placeholder={
          actionStack?.[actionStack.length - 1]?.placeholder ?? placeholder
        }
      />
      <CommandList>
        <CommandEmpty>
          {isLoading ? 'Searching...' : 'No results found.'}
        </CommandEmpty>

        {actionStack && actionStack.length > 0 && (
          <CommandGroup>
            <CommandItem onSelect={goBack}>
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              <span>Back</span>
            </CommandItem>
          </CommandGroup>
        )}

        {Object.entries(groupedActions).map(([section, sectionActions]) => (
          <CommandGroup key={section} heading={section.toUpperCase()}>
            {sectionActions.map((action) => (
              <CommandItem key={action.id} onSelect={action.onSelect}>
                <action.icon className="mr-2 h-4 w-4" />
                <span>{action.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};
